import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, MessageCircle, MessageSquare, Activity, Crown, Flame, ShoppingBag, Trophy } from "lucide-react";
import { Logo } from "@/components/Logo";
import { OnlineCounter } from "@/components/OnlineCounter";
import { RoleBadge } from "@/components/RoleBadge";

export const Nav = () => {
    const { user, logout } = useAuth();
    const nav = useNavigate();

    const handleLogout = () => {
        logout();
        nav("/");
    };

    const navLinks = [
        { to: "/", label: "Picks", testid: "nav-home-link" },
        { to: "/chat", label: "Chat", testid: "nav-chat-link", icon: MessageCircle },
        { to: "/live", label: "Live", testid: "nav-live-link", icon: Activity },
        { to: "/shop", label: "Shop", testid: "nav-shop-link", icon: ShoppingBag },
        { to: "/giveaways", label: "Giveaways", testid: "nav-giveaways-link", icon: Trophy },
        { to: "/pricing", label: "Pricing", testid: "nav-pricing-link" },
    ];
    if (user) navLinks.splice(5, 0, { to: "/messages", label: "DMs", testid: "nav-messages-link", icon: MessageSquare });

    return (
        <nav className="sticky top-0 z-50 pp-bg-0/95 backdrop-blur border-b border-[#242436]" data-testid="top-nav">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
                <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
                    <Logo size={28} />
                    <span className="font-display text-2xl tracking-widest text-white">HOTSLIP</span>
                </Link>

                <div className="hidden md:flex items-center gap-1">
                    {navLinks.map((l) => {
                        const Icon = l.icon;
                        return (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={l.testid}
                                className={({ isActive }) =>
                                    `px-3 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1 ${
                                        isActive ? "text-white bg-[#14141c]" : "text-[#b7b7c6] hover:text-white hover:bg-[#14141c]"
                                    }`
                                }
                            >
                                {Icon && <Icon className="w-4 h-4" />}
                                {l.label}
                            </NavLink>
                        );
                    })}
                    {(user?.role === "admin" || user?.role === "support") && (
                        <NavLink
                            to="/owner"
                            data-testid="nav-owner-link"
                            className={({ isActive }) =>
                                `px-3 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1 ${
                                    isActive ? "text-white bg-[#14141c]" : (user.role === "admin" ? "text-[#facc15]" : "text-[#22d3ee]") + " hover:text-white hover:bg-[#14141c]"
                                }`
                            }
                        >
                            {user.role === "admin" ? <Crown className="w-4 h-4" /> : <Flame className="w-4 h-4" />}
                            {user.role === "admin" ? "Owner" : "Staff"}
                        </NavLink>
                    )}
                </div>

                <div className="hidden md:flex items-center gap-2">
                    <OnlineCounter />
                    {!user ? (
                        <>
                            <Button asChild variant="ghost" className="text-[#b7b7c6] hover:text-white hover:bg-[#14141c]" data-testid="nav-login-link">
                                <Link to="/login">Log in</Link>
                            </Button>
                            <Button asChild className="bg-[#6f3fec] hover:brightness-110 text-white" data-testid="nav-signup-link">
                                <Link to="/signup">Sign up</Link>
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button asChild variant="ghost" className="text-[#b7b7c6] hover:text-white hover:bg-[#14141c]" data-testid="nav-account-link">
                                <Link to="/account" className="inline-flex items-center gap-2">
                                    <RoleBadge role={user.role} />
                                    <span>{user.display_name || user.email}</span>
                                </Link>
                            </Button>
                            <Button onClick={handleLogout} variant="secondary" className="bg-[#14141c] border border-[#242436] hover:bg-[#1b1b26]" data-testid="nav-logout-button">
                                Logout
                            </Button>
                        </>
                    )}
                </div>

                <Sheet>
                    <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="md:hidden text-white" data-testid="nav-mobile-menu-button">
                            <Menu className="w-5 h-5" />
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="pp-bg-0 border-[#242436] text-white">
                        <div className="flex flex-col gap-2 mt-6">
                            <div className="flex items-center justify-between mb-1"><OnlineCounter /></div>
                            {navLinks.map((l) => (
                                <Link key={l.to} to={l.to} className="py-3 px-2 rounded-md hover:bg-[#14141c] inline-flex items-center gap-2" data-testid={`mobile-${l.testid}`}>
                                    {l.label}
                                </Link>
                            ))}
                            {(user?.role === "admin" || user?.role === "support") && (
                                <Link to="/owner" className="py-3 px-2 rounded-md hover:bg-[#14141c] inline-flex items-center gap-2" style={{ color: user.role === "admin" ? "#facc15" : "#22d3ee" }}>
                                    {user.role === "admin" ? "Owner Dashboard" : "Staff Dashboard"}
                                </Link>
                            )}
                            <div className="h-px bg-[#242436] my-3" />
                            {!user ? (
                                <>
                                    <Link to="/login" className="py-3 px-2 rounded-md hover:bg-[#14141c]">Log in</Link>
                                    <Link to="/signup" className="py-3 px-2 rounded-md bg-[#6f3fec] text-white text-center">Sign up</Link>
                                </>
                            ) : (
                                <>
                                    <Link to="/account" className="py-3 px-2 rounded-md hover:bg-[#14141c] inline-flex items-center gap-2">
                                        <RoleBadge role={user.role} /> Account
                                    </Link>
                                    <button onClick={handleLogout} className="py-3 px-2 rounded-md hover:bg-[#14141c] text-left">Logout</button>
                                </>
                            )}
                        </div>
                    </SheetContent>
                </Sheet>
            </div>
        </nav>
    );
};
